import * as React from "react";
import { useState, useEffect, useCallback, useRef } from "react";
import Modal from "../../misc/components/modal";
import { Form, Checkbox, Scope, useFormApi } from "informedv4";
import { Dropdown } from "../../forms/informedv4/DropdownV4";
import { TextArea } from "../../forms/informedv4/TextAreaV4";
import { TextBox } from "../../forms/informedv4/TextBoxV4";
import { MultiSelect } from "../../forms/informedv4/MultiSelectV4";
import { DefinedReportParameterEnum } from "../../misc/api/DefinedReportParameterTypeEnum";
import LoadingWithMessage, { FontSize } from "../../misc/components/LoadingWithMessage";
import { isModelWithError, Query, Report } from "../../misc/api/apiTypes";
import Alert, { AlertCategoryEnum } from "../../misc/components/Alert";
import { DefinedReport, DefinedReportParameter, Role, WebPortalClient } from "webportalclient";
import { useWebPortalApiClient, useWebPortalLegacyClient } from "../../misc/api/WebPortalTokenClient";
import { useCurrentStudyConfigurationContext } from "../../Core/CurrentStudyConfigurationContext";

interface ReportModalProps {
    show?: boolean;
    onCloseClick?: React.MouseEventHandler;
    definedReportId?: string;
}

interface ReportModalFormProps {
    show?: boolean;
    isSaving: boolean;
    setSaving: React.Dispatch<React.SetStateAction<boolean>>;
    onCloseClick: React.MouseEventHandler;
    definedReportId?: string;
}

interface ReportModalFormState {
    path: string;
    name: string;
    description: string;
    roles: Role[];
    isWidget: boolean;
    parameters: any;
}

const ReportModalForm = ({ show = true, isSaving, setSaving, onCloseClick, definedReportId }: ReportModalFormProps) => {
    const [isReportSelected, setReportSelected] = useState(false);
    const [reports, setReports] = useState([]);
    const [definedReport, setDefinedReport] = useState<DefinedReport>(null);
    const [report, setReport] = useState(null);
    const [userGroups, setUserGroups] = useState<Role[]>([]);
    const [reportParameters, setReportParameters] = useState([]);
    const [isFetchingReports, setFetchingReports] = useState(false);
    const [isFetchingReportParameters, setFetchingReportParameters] = useState(false);
    const [reportParametersError, setReportParametersError] = useState(false);
    const [saveErrorMessage, setSaveErrorMessage] = useState<string>(null);
    const [loadingValues, setLoadingValues] = useState(false);
    const client = useWebPortalApiClient();
    const legacyClient = useWebPortalLegacyClient();
    const { study } = useCurrentStudyConfigurationContext();

    // HACK: Ref required until the informed library is updated to latest version that uses hooks...
    const reportPathRef = useRef();

    const formApi = useFormApi();

    useEffect(() => {
        const getDefinedReport = async () => {
            const definedRep = await client.client.getDefinedReport(definedReportId);
            setDefinedReport(definedRep);
            setLoadingValues(true);
        }

        if (definedReportId) {
            getDefinedReport();
        }
    }, []);

    useEffect(
        () => {
            if (reports.length > 0) {
                let rep: any = {};
                if (report)
                    rep = reports.find(item => item.Id == report);
                else if (definedReport)
                    rep = reports.find(item => item.Path == definedReport.path);

                if (rep) {
                    formApi.reset();
                    formApi.setValue("path", rep.Id);
                    reportPathRef.current = rep.Path;
                    setReport(rep.Id);
                    setReportParameters([]);
                    setReportSelected(true);
                }
            }
        }, [reports, report, definedReport]);

    useEffect(() => {
        if (report && definedReport) {
            formApi.setValue("name", definedReport.name);
            formApi.setValue("description", definedReport.description);
            formApi.setValue("isWidget", definedReport.isWidget);
        }
    }, [definedReport, report]);

    useEffect(() => {
        if (report && definedReport && userGroups.length > 0) {
            const roles = definedReport.rolesIds.map(r => userGroups.find(g => g.id == r));
            formApi.setValue("roles", roles);
            setLoadingValues(false);
        }
    }, [definedReport, userGroups, report])

    useEffect(() => {
        if (definedReport && reportParameters.length > 0) {
            for (let p of definedReport.parameters) {
                formApi.setValue(`parameters.${p.name}.type`, p.parameterTypeId);
            }
        }
    }, [definedReport, reportParameters]);

    useEffect(() => {
        let isCanceled = false;

        setFetchingReports(true);

        const initialQuery: Query<Report> = {};
        legacyClient.getReports(initialQuery).then(query => {
            setFetchingReports(false);
            if (!isCanceled) setReports(query.Data ? query.Data : []);
        });

        return () => { isCanceled = true };
    }, []);

    useEffect(() => {
        let isCanceled = false;

        client.client.getRoles(undefined, undefined, `name asc`)
        .then(query => {
            if (!isCanceled) setUserGroups(query.value ? query.value : []);
        });

        return () => { isCanceled = true };
    }, []);

    useEffect(
        () => {
            let isCanceled = false;

            if (report) {
                setFetchingReportParameters(true);
                legacyClient.getReportParameters(report).then(data => {
                    setFetchingReportParameters(false);
                    if (isModelWithError(data)) {
                        return setReportParametersError(true);
                    }

                    if (!isCanceled)
                        setReportParameters(data ? data : []);
                });
            }

            return () => {
                isCanceled = true;
                setReportParametersError(false);
            };
        },
        [report]
    );

    useEffect(
        () => {
            if (show) {
                setReportSelected(false);
                setSaving(false);
            }
        },
        [show]
    );

    const reFormatParameters = (parameters): DefinedReportParameter[] => {
        if (!parameters)
            return [];

        var result = [];
        for (var param of reportParameters) {
            var parameter: DefinedReportParameter = { name: param.Name, parameterTypeId: parameters[param.Name]?.type ?? null };
            result.push(parameter);
        }
        return result;
    }

    const handleSubmit = async () => {
        setSaveErrorMessage(null);
        setSaving(true);
        let state = formApi.getFormState();

        formApi.validate();

        if (!definedReportId)
            await validateUniqueReportName(state.values.name);

        state = formApi.getFormState();
        if (state.invalid) {
            setSaving(false);
            return;
        }

        const report: DefinedReport = {
            path: reportPathRef.current,
            name: state.values.name as string,
            description: state.values.description as string,
            rolesIds: (state.values.roles as any[]).map(r => r.id),
            parameters: reFormatParameters(state.values.parameters),
            isWidget: state.values.isWidget as boolean
        }
        try {
            definedReportId ? await client.client.patchDefinedReport(report, definedReportId) : await client.client.postDefinedReport(report);
            onCloseClick(null);
        } catch (error) {
            setSaveErrorMessage(error.message);
            setSaving(false);
        }
    }

    const isWhitespaceString = str => str && !str.toString().replace(/\s/g, '').length;
    const handleTextInputRequiredValidate = value => (!value || isWhitespaceString(value) ? "Field is required." : undefined);

    const handleRequiredValidate = value => (!value ? "Field is required." : undefined);

    const validateUniqueReportName = async name => {
        if (!name)
            return;

        const definedReports = await client.client.getDefinedReports("name", `studyIds/any(s: s eq ${study.id})`);
        const exists = definedReports.value.find(item => item.name.toLowerCase().trim() == name.toLowerCase().trim());

        if (exists)
            formApi.setError("name", "Report name already exists.");
    };

    return (
        <>
            <div className="modal-body">
                <Alert show={saveErrorMessage !== null} category={AlertCategoryEnum.Danger}>
                    {saveErrorMessage}
                </Alert>
                <div className="form-group row">
                    <div className="col-sm-3">
                        <label className="pull-right">Report</label>
                    </div>
                    <div className="col-sm-9">
                        <p>Select a report to setup.</p>
                        {reports && !!reports.length ? (
                            <Dropdown
                                name="path"
                                field="path"
                                className="form-control"
                                validate={handleRequiredValidate}
                                value=""
                                disabled={isSaving || loadingValues}
                                onChange={(e) => setReport((e as any).target.value)}
                            >
                                <option value="" disabled />
                                {reports.map(item => (
                                    <option key={item.Id} value={item.Id}>
                                        {item.Path}
                                    </option>
                                ))}
                            </Dropdown>
                        ) : isFetchingReports ? (
                            <LoadingWithMessage
                                size={FontSize.OneX}
                                message="Loading reports."
                            />
                        ) : (
                            "No reports available."
                        )}
                    </div>
                </div>
                <div className="form-group row">
                    <div className="col-sm-3">
                        <label className="pull-right">Name</label>
                    </div>
                    <div className="col-sm-9">
                        <p>Enter a title to display for the report.</p>
                        <TextBox
                            name="name"
                            field="name"
                            className="form-control"
                            validate={handleTextInputRequiredValidate}
                            //asyncValidate={validateUniqueReportName}
                            disabled={!isReportSelected || isSaving || loadingValues}
                        />
                    </div>
                </div>
                <div className="form-group row">
                    <div className="col-sm-3">
                        <label className="pull-right">Description</label>
                    </div>
                    <div className="col-sm-9">
                        <p>Enter a description of the report report.</p>
                        <TextArea
                            name="description"
                            field="description"
                            style={{ resize: "none" }}
                            className="form-control"
                            validate={handleTextInputRequiredValidate}
                            disabled={!isReportSelected || isSaving || loadingValues}
                        />
                    </div>
                </div>
                <div className="form-group row">
                    <div className="col-sm-3">
                        <label className="pull-right">User Roles</label>
                    </div>
                    <div className="col-sm-9">
                        <p>
                            Select one or more user roles to allow access to the
                            report.
                        </p>
                        <MultiSelect
                            name="roles"
                            id="roles"
                            field="roles"
                            validate={handleRequiredValidate}
                            multi
                            valueKey="id"
                            labelKey="name"
                            options={userGroups}
                            disabled={!isReportSelected || isSaving || loadingValues}
                        />
                    </div>
                </div>
                <div className="form-group row">
                    <div className="col-sm-3">
                        <label className="pull-right">Widget</label>
                    </div>
                    <div className="col-sm-9">
                        <p>
                            Mark this report as a widget to display on the
                            dashboard.
                        </p>
                        <Checkbox
                            name="isWidget"
                            field="isWidget"
                            disabled={!isReportSelected || isSaving || loadingValues}
                        />
                    </div>
                </div>
                {!isFetchingReportParameters &&
                    reportParameters &&
                    reportParameters.length > 0 ? (
                    <div className="form-group row">
                        <div className="col-sm-3">
                            <label className="pull-right">Parameters</label>
                        </div>
                        <div className="col-sm-9">
                            <p>Select how report parameters should be populated.</p>
                        </div>
                    </div>
                ) : isFetchingReportParameters ? (
                    <LoadingWithMessage
                        size={FontSize.OneX}
                        message="Loading report parameters."
                    />
                ) : reportParametersError ? (
                    <Alert show={true} category={AlertCategoryEnum.Danger}>
                        Failed to load report parameters
                    </Alert>
                ) : null}
                {reportParameters.map((item, index) => (
                    <Scope key={item.Name} scope={`parameters.${item.Name}`}>
                        <div className="form-group row">
                            <div className="col-sm-3">
                                <label className="pull-right">
                                    <i>{item.Prompt || item.Name}</i>
                                </label>
                            </div>
                            <div className="col-sm-9">
                                <Dropdown
                                    name="type"
                                    field="type"
                                    className="form-control"
                                    value=""
                                    disabled={!isReportSelected || isSaving || loadingValues}
                                    data-parameter-name={item.Name}
                                >
                                    <option value="" />
                                    <option
                                        value={DefinedReportParameterEnum.UserId}
                                    >
                                        User
                                    </option>
                                    <option
                                        value={DefinedReportParameterEnum.StudyId}
                                    >
                                        Study
                                    </option>
                                </Dropdown>
                            </div>
                        </div>
                    </Scope>
                ))}
            </div>
            <div className="modal-footer">
                <button
                    type="submit"
                    onClick={handleSubmit}
                    className="btn btn-primary"
                    disabled={
                        isSaving || isFetchingReports || isFetchingReportParameters
                    }
                >
                    Save
                </button>
                <button
                    className="btn btn-danger"
                    disabled={isSaving}
                    onClick={onCloseClick}
                >
                    Cancel
                </button>
            </div>
        </>
    );
};

export const ReportModal = ({ show = true, onCloseClick, definedReportId: definedReportId }: ReportModalProps) => {
    const [isSaving, setSaving] = useState(false);

    return (
        <Modal
            show={show}
            header={definedReportId ? "Edit Report" : "Add Report"}
            closeModalHandler={e => (isSaving ? null : onCloseClick(e))}
        >
            <Form validateOn="submit">
                <ReportModalForm definedReportId={definedReportId} show={show} onCloseClick={onCloseClick} isSaving={isSaving} setSaving={setSaving} />
            </Form>
        </Modal>
    );
}

export default ReportModal;
